"use client";

import {
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import { buildAttributionSnapshot } from "@/app/lib/attributionSnapshot";
import { launchConfetti } from "@/app/lib/confetti";
import { formatPragueTimestamp } from "@/app/lib/formatTime";
import styles from "./ArcadePrizeMachine.module.css";

type Sprite = {
  name: string;
  desc: string;
  gold?: boolean;
  pal: Record<string, string>;
  map: string[];
};

type Prize = {
  key: string;
  s: Sprite;
  w: number;
  h: number;
  cx: number;
  x: number;
  y: number;
  falling: boolean;
  vy: number;
  inChute: boolean;
  collected: boolean;
  attached: boolean;
};

type GameState =
  | "attract"
  | "play"
  | "drop"
  | "close"
  | "lift"
  | "carry"
  | "openC"
  | "chuteWait"
  | "emptyWait"
  | "result";

type ResultKind = "gold" | "junk" | "slip" | "empty";

type ResultState = {
  kind: ResultKind;
  prize: Prize | null;
  title: string;
  text: string;
  note?: string;
};

type ClaimState = "idle" | "submitting" | "success" | "error";

type ArcadeLocale = "cs" | "en";

type Props = {
  variant?: string;
  locale?: ArcadeLocale;
};

const CFG = {
  CLAIM_ENDPOINT: "/api/arcade-code",
  BONUS_CREDITS_ENDPOINT: "/api/arcade-credits",
  REWARD_ID: "discount_2500_first_month",
  BONUS_REWARD_ID: "bonus_credits_3",
  REWARD_VALUE: "2 500 Kč",
  COUPON: "TMRW-DRAPAK-2500",
  SLIP_JUNK: 0.18,
  SLIP_GOLD: 0.35,
  DROP_SECONDS: 6,
  CREDITS: 3,
  BONUS_CREDITS: 3,
} as const;

const STRINGS = {
  cs: {
    machineName: "VÝHERNÍ AUTOMAT",
    subtitle: "3 mince. 1 výhra.",
    guideTitle: "NÁVOD",
    aim: "Zamiř šipkami nebo podrž stranu automatu.",
    coinsLeft: (n: number) =>
      n === 1 ? "zbývá poslední mince" : `zbývají ${n} mince`,
    outOfCoinsNote: "Došly vám mince.",
    insertCoin: "⬤ VHODIT MINCI",
    outOfCoins: "DOŠLY MINCE",
    bonusTitle: "CHCETE DALŠÍ TŘI MINCE?",
    bonusText: "Zadejte e-mail a odemkneme vám ještě tři pokusy.",
    bonusEmailAriaLabel: "E-mail",
    bonusConsentPrefix: "Souhlasím se zpracováním e-mailu dle",
    privacyPolicy: "zásad pro ochranu osobních údajů",
    bonusSubmit: "ZÍSKAT 3 MINCE ▸",
    bonusSending: "Přidáváme mince…",
    bonusSuccess: "Hotovo - máte další tři mince.",
    bonusError: "Mince se nepodařilo přidat. Zkuste to prosím znovu.",
    bonusUsedTitle: "VŠECH 6 MINCÍ JE PRYČ",
    bonusUsedText: "Další trojici pokusů už jste využili.",
    missingConsent: "Potvrďte prosím souhlas se zpracováním údajů.",
    drop: "SPUSTIT ▼",
    dropCountdown: (s: string) => `SPUSTIT ▼ ${s}`,
    dropping: "LETÍ…",
    droppingAuto: "LETÍ SÁM…",
    hint: "← → · mezerník · nebo drž stranu automatu",
    left: "doleva",
    right: "doprava",
    sound: "zvuk",
    slipTitle: "VYKLOUZLO TI TO",
    slipText: "Bylo to skoro tvoje.",
    emptyTitle: "PRÁZDNÝ DRAPÁK",
    emptyNote: "Statisticky nejčastější výhra.",
    slipLines: [
      "Drapák: „Promiň.\"",
      "Fyzika 1 : 0 ty.",
      "Nešlo to. Doslova.",
      "Krásných 0,8 vteřiny to bylo tvoje.",
    ],
    emptyLines: [
      "Chytil přesně nic.",
      "Vzduch. Prémiová edice.",
      "Vrátil se s ničím. Zase.",
    ],
    junkQuips: [
      "Zlatý kupón tam pořád leží.",
      "Bylo to skoro tvoje.",
      "Chytil jsi vzduch. Premium edition.",
    ],
    rewardText:
      "Sleva 2 500 Kč na první měsíc je vaše. Platí 90 dní, jen pro nové klienty.",
    emailLabel: "E-mail pro kód",
    emailPlaceholder: "vas@email.cz",
    consent:
      "Souhlasím se zpracováním osobních údajů pro marketingovou komunikaci.",
    companyLabel: "Firma",
    companyOptional: "(volitelné)",
    companyPlaceholder: "Název firmy",
    claimNote:
      "Kód pošleme e-mailem. Sleva platí 90 dní pro nové klienty a vztahuje se na fee prvního měsíce.",
    sendCode: "POSLAT KÓD ▸",
    sent: "ODESLÁNO",
    playAgain: "HRÁT ZNOVU ▸",
    tryAgain: "DALŠÍ POKUS ▸",
    missingEmail: "Doplňte e-mail.",
    sending: "Posíláme kód…",
    sentOk: "Kód je na cestě. Mrkněte do e-mailu.",
    sentErr: "Kód se nepodařilo poslat. Zkuste to prosím znovu.",
  },
  en: {
    machineName: "PRIZE MACHINE",
    subtitle: "3 coins. 1 win.",
    guideTitle: "HOW TO PLAY",
    aim: "Aim with the arrows or hold a side of the machine.",
    coinsLeft: (n: number) => (n === 1 ? "last coin left" : `${n} coins left`),
    outOfCoinsNote: "You're out of coins.",
    insertCoin: "⬤ INSERT COIN",
    outOfCoins: "OUT OF COINS",
    bonusTitle: "WOULD YOU LIKE THREE MORE COINS?",
    bonusText: "Enter your e-mail to unlock three more tries.",
    bonusEmailAriaLabel: "E-mail",
    bonusConsentPrefix: "I consent to the processing of my e-mail under the",
    privacyPolicy: "privacy policy",
    bonusSubmit: "GET 3 COINS ▸",
    bonusSending: "Adding coins…",
    bonusSuccess: "Done - you have three more coins.",
    bonusError: "We couldn't add the coins. Please try again.",
    bonusUsedTitle: "ALL 6 COINS ARE GONE",
    bonusUsedText: "You've already used your extra three tries.",
    missingConsent: "Please consent to the processing of your data.",
    drop: "DROP ▼",
    dropCountdown: (s: string) => `DROP ▼ ${s}`,
    dropping: "DROPPING…",
    droppingAuto: "AUTO-DROP…",
    hint: "← → · spacebar · or hold a side of the machine",
    left: "left",
    right: "right",
    sound: "sound",
    slipTitle: "IT SLIPPED AWAY",
    slipText: "It was almost yours.",
    emptyTitle: "EMPTY CLAW",
    emptyNote: "Statistically the most common prize.",
    slipLines: [
      "Claw: \"Sorry.\"",
      "Physics 1 : 0 you.",
      "It just didn't hold. Literally.",
      "It was yours for a beautiful 0.8 seconds.",
    ],
    emptyLines: [
      "Caught exactly nothing.",
      "Air. Premium edition.",
      "Came back empty. Again.",
    ],
    junkQuips: [
      "The golden coupon is still in there.",
      "It was almost yours.",
      "You caught air. Premium edition.",
    ],
    rewardText:
      "CZK 2,500 off your first month is yours. Valid for 90 days, new clients only.",
    emailLabel: "E-mail for the code",
    emailPlaceholder: "you@email.com",
    consent:
      "I consent to the processing of my personal data for marketing communication.",
    companyLabel: "Company",
    companyOptional: "(optional)",
    companyPlaceholder: "Company name",
    claimNote:
      "We'll email you the code. The discount is valid for 90 days for new clients and applies to the first month's fee.",
    sendCode: "SEND CODE ▸",
    sent: "SENT",
    playAgain: "PLAY AGAIN ▸",
    tryAgain: "ONE MORE TRY ▸",
    missingEmail: "Add your e-mail.",
    sending: "Sending the code…",
    sentOk: "The code is on its way. Check your inbox.",
    sentErr: "We couldn't send the code. Please try again.",
  },
} as const;

const PRIZE_EN: Record<string, { name: string; desc: string }> = {
  kaktus: { name: "PIXEL CACTUS", desc: "Don't water it. It dies anyway, just slower." },
  banner: { name: "BANNER FROM 2009", desc: "CLICK HERE!!! It would blink if it could." },
  brief: { name: "UNUSED BRIEF", desc: "The client ended up doing it themselves." },
  lista: { name: "COOKIE BANNER", desc: "Congrats. It's yours now. Forever." },
  disketa: { name: "\"BACKUP\" FLOPPY", desc: "Nobody knows what's on it. Not even us." },
  hrnek: { name: "MONDAY COFFEE", desc: "Went cold sometime Tuesday. Do not drink." },
  faktura: { name: "OVERDUE INVOICE", desc: "Collector's edition. Sadly very common." },
  ponozka: { name: "ODD SOCK", desc: "From the office. Still looking for its pair." },
  vizitka: { name: "CONFERENCE BUSINESS CARD", desc: "Someone. From somewhere. They'll be in touch." },
  postit: { name: "PASSWORD POST-IT", desc: "\"P@ssw0rd123\". Safe - it's on paper, after all." },
  razitko: { name: "RUBBER STAMP", desc: "Digitalization arrived. The stamp stayed." },
  meeting: { name: "WEDNESDAY MEETING", desc: "Could have been an email." },
  nokia: { name: "NOKIA CHARGER", desc: "Works perfectly. With nothing you own." },
  usb: { name: "TRADE FAIR USB", desc: "2 GB. Contains a deck from 2017." },
  donut: { name: "THE LAST DONUT", desc: "The one nobody dared to take." },
  zlaty: { name: "GOLDEN COUPON", desc: "The only win worth anything here." },
};

function prizeCopy(key: string, locale: ArcadeLocale) {
  if (locale === "en" && PRIZE_EN[key]) return PRIZE_EN[key];
  const sprite = SPRITES[key];
  return { name: sprite.name, desc: sprite.desc };
}

const W = 200;
const H = 270;
const FLOOR = 222;
const CHUTE = { x: 12, w: 28, cx: 26 };
const PLAY_MIN = 46;
const PLAY_MAX = 176;

const SPRITES: Record<string, Sprite> = {
  kaktus: {
    name: "PIXELOVÝ KAKTUS",
    desc: "Nezalévat. Umře tak jako tak, ale pomaleji.",
    pal: { G: "#4FAE4F", P: "#B06A3B", B: "#7C4526" },
    map: [
      "....GG....",
      "....GG....",
      "GG..GG..GG",
      "GG..GG..GG",
      "GGGGGG..GG",
      "....GGGGGG",
      "....GG....",
      "...PPPP...",
      "...PBBP...",
      "...PPPP...",
    ],
  },
  banner: {
    name: "BANNER Z ROKU 2009",
    desc: "KLIKNI ZDE!!! Kdyby mohl, blikal by.",
    pal: { M: "#FF4FD8", Y: "#FFE14D", C: "#4DD9FF", K: "#111111" },
    map: [
      "MMMMMMMMMMMM",
      "MYCYCYCYCYCM",
      "MCKCKCKCKCYM",
      "MYCYCYCYCYCM",
      "MCYKYKYKYCYM",
      "MMMMMMMMMMMM",
    ],
  },
  brief: {
    name: "NEPOUŽITÝ BRIEF",
    desc: "Klient si to nakonec udělal sám.",
    pal: { W: "#F5F1E6", L: "#9A93A8", F: "#CFC9BB" },
    map: [
      "WWWWWWWF..",
      "WWWWWWWFF.",
      "WLLLLLWWW.",
      "WWWWWWWWW.",
      "WLLLLLLLW.",
      "WWWWWWWWW.",
      "WLLLLWWWW.",
      "WWWWWWWWW.",
    ],
  },
  lista: {
    name: "COOKIE LIŠTA",
    desc: "Gratulujeme. Teď je tvoje. Navždy.",
    pal: { G: "#4A4358", W: "#F5F1E6", R: "#FC473C" },
    map: ["GGGGGGGGGGGG", "GWWWWW...RRG", "GWWW.....RRG", "GGGGGGGGGGGG"],
  },
  disketa: {
    name: "DISKETA „ZÁLOHA\"",
    desc: "Nikdo neví, co na ní je. Ani my.",
    pal: { B: "#3D6BFF", S: "#C9D1FF", W: "#F5F1E6", D: "#22409E" },
    map: [
      "BBBBBBBBB.",
      "BSSSSSSBB.",
      "BSSSSSSBBB",
      "BBBBBBBBBB",
      "BBWWWWWWBB",
      "BBWDDDWWBB",
      "BBWWWWWWBB",
      "BBBBBBBBBB",
    ],
  },
  hrnek: {
    name: "PONDĚLNÍ KAFE",
    desc: "Vychladlo někdy v úterý. Nepít.",
    pal: { W: "#F5F1E6", K: "#5A3B23", S: "#BDB6A6" },
    map: [
      ".WWWWWW...",
      ".WKKKKW.W.",
      ".WKKKKW.WW",
      ".WKKKKW.W.",
      ".WWWWWW...",
      "..SSSS....",
    ],
  },
  faktura: {
    name: "FAKTURA PO SPLATNOSTI",
    desc: "Sběratelská edice. Bohužel velmi rozšířená.",
    pal: { W: "#F5F1E6", L: "#9A93A8", R: "#FC473C" },
    map: [
      "WWWWWWWWW.",
      "WLLLLLLWW.",
      "WWWWWWWWW.",
      "WLLLWWWWW.",
      "WWWWWRRWW.",
      "WWWWWRRWW.",
      "WWWWWWWWW.",
      "WWWWWRRWW.",
    ],
  },
  ponozka: {
    name: "LICHÁ PONOŽKA",
    desc: "Z kanclu. Párovou hledáme dodnes.",
    pal: { P: "#7AD1FF", D: "#3F9FD4", W: "#F5F1E6" },
    map: [
      "..WWWW....",
      "..PPPP....",
      "..PPPP....",
      "..PPPP....",
      ".PPPPP....",
      "PPPPPP....",
      "PDDPPP....",
      ".PPPP.....",
    ],
  },
  vizitka: {
    name: "VIZITKA Z KONFERENCE",
    desc: "Někdo. Odněkud. Prý se ozve.",
    pal: { W: "#F5F1E6", L: "#9A93A8", A: "#FC473C" },
    map: [
      "WWWWWWWWWWWW",
      "WAAW.LLLLLLW",
      "WAAW.......W",
      "W....LLLL..W",
      "W....LLL...W",
      "WWWWWWWWWWWW",
    ],
  },
  postit: {
    name: "POST-IT S HESLEM",
    desc: "„P@ssw0rd123\". Bezpečné - je to přece na papíře.",
    pal: { Y: "#FFE14D", L: "#8A6E10", D: "#C9A72E" },
    map: [
      "YYYYYYYY",
      "YLLLLYYY",
      "YYYYYYYY",
      "YLLLYYYY",
      "YYYYYYYY",
      "YYYYYYDD",
    ],
  },
  razitko: {
    name: "RAZÍTKO",
    desc: "Digitalizace dorazila. Razítko zůstalo.",
    pal: { K: "#26262C", R: "#FC473C" },
    map: ["..KKKK..", "...KK...", "..KKKK..", ".RRRRRR.", "RRRRRRRR"],
  },
  meeting: {
    name: "PORADA VE STŘEDU",
    desc: "Mohl to být e-mail.",
    pal: { R: "#FC473C", W: "#F5F1E6", K: "#26262C" },
    map: [
      "RRRRRRRR",
      "WWWWWWWW",
      "WKWKWKWW",
      "WWWWWWWW",
      "WKWKWWWW",
      "WWWWWWWW",
    ],
  },
  nokia: {
    name: "NABÍJEČKA OD NOKIA",
    desc: "Funguje perfektně. Na nic, co vlastníš.",
    pal: { K: "#26262C", L: "#5A5A64" },
    map: [
      "KKKKK...",
      "KKKKK...",
      "KKKKK...",
      "..K.....",
      "..K..LL.",
      ".LLLLL..",
    ],
  },
  usb: {
    name: "USB Z VELETRHU",
    desc: "2 GB. Uvnitř prezentace z roku 2017.",
    pal: { S: "#B9BCC4", B: "#2B4BFF", R: "#FC473C" },
    map: ["SS.BBBBBBB", "SSBBBBBBBB", "SSBBBRRBBB", "SSBBBBBBBB", "SS.BBBBBBB"],
  },
  donut: {
    name: "POSLEDNÍ DONUT",
    desc: "Ten, co si nikdo netroufl vzít.",
    pal: { P: "#FF9EC4", H: "#7A4A22", S: "#2B4BFF" },
    map: [
      "..PPPPPP..",
      ".PPSPPSPP.",
      "PPPPHHPPPP",
      "PPSPHHPSPP",
      ".PPPPPPPP.",
      "..PPPPPP..",
    ],
  },
  zlaty: {
    name: "ZLATÝ KUPÓN",
    desc: "Jediná výhra, co tu má hodnotu.",
    gold: true,
    pal: { G: "#F2B33C", D: "#C08A1E", W: "#FFF3D4" },
    map: [
      ".GGGGGGGGGG.",
      "GGDDDDDDDDGG",
      "GD..WWW...DG",
      "GD.WWWWW..DG",
      "GD..WWW...DG",
      "GGDDDDDDDDGG",
      ".GGGGGGGGGG.",
    ],
  },
};

const TYPE_KEYS = Object.keys(SPRITES);

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function px(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
) {
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(x), Math.round(y), w, h);
}

function drawSprite(
  ctx: CanvasRenderingContext2D,
  sprite: Sprite,
  x: number,
  y: number,
) {
  sprite.map.forEach((row, ry) => {
    for (let rx = 0; rx < row.length; rx += 1) {
      const ch = row[rx];
      if (ch !== ".") {
        ctx.fillStyle = sprite.pal[ch];
        ctx.fillRect(Math.round(x) + rx, Math.round(y) + ry, 1, 1);
      }
    }
  });
}

export default function ArcadePrizeMachine({
  variant = "automat_short_copy",
  locale = "cs",
}: Props) {
  const t = STRINGS[locale];
  const rootRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const audioRef = useRef<AudioContext | null>(null);
  const stateRef = useRef<GameState>("attract");
  const clawRef = useRef({ x: 111, y: 34, open: 1, vx: 0 });
  const prizesRef = useRef<Prize[]>([]);
  const attachedPrizeRef = useRef<Prize | null>(null);
  const resultPrizeRef = useRef<Prize | null>(null);
  const slipYRef = useRef<number | null>(null);
  const slippedRef = useRef(false);
  const waitFramesRef = useRef(0);
  const holdLeftRef = useRef(false);
  const holdRightRef = useRef(false);
  const pointerDownRef = useRef(false);
  const tickThrottleRef = useRef(0);
  const creditsRef = useRef<number>(CFG.CREDITS);
  const attemptRef = useRef(0);
  const dropTimeRef = useRef(0);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(0);
  const loopActiveRef = useRef(false);
  const canvasInViewRef = useRef(true);
  const reduceMotionRef = useRef(false);
  const mutedRef = useRef(false);

  const [credits, setCredits] = useState<number>(CFG.CREDITS);
  const [muted, setMuted] = useState(false);
  const [coinVisible, setCoinVisible] = useState(true);
  const [dropDisabled, setDropDisabled] = useState(true);
  const [dropLabel, setDropLabel] = useState<string>(t.drop);
  const [result, setResult] = useState<ResultState | null>(null);
  const [claimEmail, setClaimEmail] = useState("");
  const [claimCompany, setClaimCompany] = useState("");
  const [claimMarketing, setClaimMarketing] = useState(false);
  const [claimState, setClaimState] = useState<ClaimState>("idle");
  const [claimMessage, setClaimMessage] = useState("");
  const [bonusEmail, setBonusEmail] = useState("");
  const [bonusConsent, setBonusConsent] = useState(false);
  const [bonusClaimed, setBonusClaimed] = useState(false);
  const [bonusNotice, setBonusNotice] = useState(false);
  const [bonusState, setBonusState] = useState<ClaimState>("idle");
  const [bonusMessage, setBonusMessage] = useState("");

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const emitArcadeEvent = useCallback(
    (
      event: "arcade_start" | "arcade_result" | "arcade_email" | "arcade_replay",
      payload: Record<string, unknown>,
    ) => {
      window.dataLayer?.push({
        event,
        game: "automat",
        variant,
        locale,
        ...payload,
      });
    },
    [locale, variant],
  );

  const audio = useCallback(() => {
    const AudioCtor =
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;

    if (!audioRef.current && AudioCtor) {
      audioRef.current = new AudioCtor();
    }

    return audioRef.current;
  }, []);

  const tone = useCallback(
    (
      frequency: number,
      duration: number,
      type: OscillatorType = "square",
      gain = 0.06,
      when = 0,
    ) => {
      if (mutedRef.current) return;

      try {
        const context = audio();
        if (!context) return;

        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        oscillator.type = type;
        oscillator.frequency.value = frequency;
        gainNode.gain.setValueAtTime(gain, context.currentTime + when);
        gainNode.gain.exponentialRampToValueAtTime(
          0.0001,
          context.currentTime + when + duration,
        );
        oscillator.connect(gainNode).connect(context.destination);
        oscillator.start(context.currentTime + when);
        oscillator.stop(context.currentTime + when + duration + 0.02);
      } catch {
        // Audio is non-essential and can be blocked by browser policy.
      }
    },
    [audio],
  );

  const soundCoin = useCallback(() => {
    tone(988, 0.07);
    tone(1319, 0.1, "square", 0.06, 0.07);
  }, [tone]);

  const soundTick = useCallback(() => tone(190, 0.03, "square", 0.03), [tone]);

  const soundClack = useCallback(
    () => tone(120, 0.07, "square", 0.08),
    [tone],
  );

  const soundSlip = useCallback(() => {
    tone(300, 0.12, "sawtooth", 0.06);
    tone(170, 0.18, "sawtooth", 0.06, 0.11);
  }, [tone]);

  const soundJunk = useCallback(() => {
    tone(392, 0.09);
    tone(330, 0.13, "square", 0.05, 0.09);
  }, [tone]);

  const soundEmpty = useCallback(() => {
    tone(220, 0.12, "sawtooth", 0.05);
    tone(150, 0.16, "sawtooth", 0.05, 0.11);
  }, [tone]);

  const soundWin = useCallback(() => {
    [523, 659, 784, 1046, 1319].forEach((frequency, index) => {
      tone(frequency, 0.13, "square", 0.06, index * 0.11);
    });
  }, [tone]);

  const resetRound = useCallback(() => {
    const order = [...TYPE_KEYS];
    for (let i = order.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }

    prizesRef.current = order
      .map((key, index) => {
        const sprite = SPRITES[key];
        const w = sprite.map[0].length;
        const h = sprite.map.length;
        const cx = Math.round(52 + index * 8.6 + (Math.random() * 4 - 2));
        return {
          key,
          s: sprite,
          w,
          h,
          cx,
          x: cx - Math.round(w / 2),
          y: FLOOR - h - Math.floor(Math.random() * 3),
          falling: false,
          vy: 0,
          inChute: false,
          collected: false,
          attached: false,
        };
      })
      .sort((a, b) => a.cx - b.cx);

    attachedPrizeRef.current = null;
    resultPrizeRef.current = null;
    slipYRef.current = null;
    slippedRef.current = false;
    clawRef.current = { x: 111, y: 34, open: 1, vx: 0 };
  }, []);

  const drawClaw = useCallback((ctx: CanvasRenderingContext2D) => {
    const claw = clawRef.current;
    const cx = Math.round(claw.x);
    const cy = Math.round(claw.y);
    px(ctx, cx - 1, 25, 2, cy - 31, "#8A8A8A");
    px(ctx, cx - 5, cy - 8, 10, 5, "#C9CBD1");
    px(ctx, cx - 5, cy - 8, 10, 1, "#EDEDF0");
    const spread = 2 + Math.round(claw.open * 4);
    px(ctx, cx - spread - 2, cy - 4, 2, 7, "#EDEDF0");
    px(ctx, cx - spread - 2, cy + 3, 3, 2, "#EDEDF0");
    px(ctx, cx + spread, cy - 4, 2, 7, "#EDEDF0");
    px(ctx, cx + spread - 1, cy + 3, 3, 2, "#EDEDF0");
  }, []);

  const render = useCallback(
    (time = 0) => {
      const ctx = ctxRef.current;
      if (!ctx) return;

      px(ctx, 0, 0, W, H, "#FC473C");
      px(ctx, 5, 5, W - 10, H - 10, "#D8362C");
      px(ctx, 5, 5, W - 10, 16, "#0A0A0A");
      px(ctx, 9, 21, 182, 4, "#8A8A8A");
      px(ctx, 7, 25, 186, 211, "#0A0A0A");
      px(ctx, 9, 27, 182, 195, "#121216");

      prizesRef.current.forEach((prize) => {
        if (!prize.collected && !prize.attached) {
          drawSprite(ctx, prize.s, prize.x, prize.y);
        }
      });

      const attached = attachedPrizeRef.current;
      if (attached) {
        drawSprite(ctx, attached.s, attached.x, attached.y);
      }

      drawClaw(ctx);

      px(ctx, 9, FLOOR, 3, 12, "#1A1A1E");
      px(ctx, CHUTE.x + CHUTE.w, FLOOR, 191 - CHUTE.x - CHUTE.w, 12, "#1A1A1E");
      px(ctx, 9, FLOOR, 3, 2, "#33333A");
      px(ctx, CHUTE.x + CHUTE.w, FLOOR, 191 - CHUTE.x - CHUTE.w, 2, "#33333A");
      px(ctx, CHUTE.x, FLOOR, CHUTE.w, 12, "#000000");
      px(ctx, CHUTE.x, FLOOR - 2, CHUTE.w, 2, "#F2B33C");
      px(ctx, 5, 236, W - 10, H - 241, "#D8362C");
      px(ctx, 12, 242, 44, 22, "#F2B33C");
      px(ctx, 15, 245, 38, 16, "#0A0A0A");
      px(ctx, 30, 251, 8, 2, "#F2B33C");
      px(ctx, 32, 254, 4, 2, "#F2B33C");
      ctx.globalAlpha = 0.05;
      px(ctx, 28, 30, 7, 190, "#FFFFFF");
      px(ctx, 42, 30, 3, 190, "#FFFFFF");
      ctx.globalAlpha = 1;

      if (!reduceMotionRef.current && Math.floor(time / 400) % 2 === 0) {
        px(ctx, 16, 10, 3, 3, "#FC473C");
        px(ctx, 181, 10, 3, 3, "#2B4BFF");
      } else {
        px(ctx, 181, 10, 3, 3, "#FC473C");
        px(ctx, 16, 10, 3, 3, "#2B4BFF");
      }
    },
    [drawClaw],
  );

  const showResult = useCallback(
    (prize: Prize | null) => {
      stateRef.current = "result";
      setDropDisabled(true);
      setDropLabel(t.drop);
      setClaimEmail("");
      setClaimCompany("");
      setClaimMarketing(false);
      setClaimState("idle");
      setClaimMessage("");

      let nextResult: ResultState;
      let resultName: "gold" | "junk" | "empty" | "slip";

      if (prize?.s.gold) {
        soundWin();
        launchConfetti();
        resultName = "gold";
        const copyGold = prizeCopy(prize.key, locale);
        nextResult = {
          kind: "gold",
          prize,
          title: copyGold.name,
          text: copyGold.desc,
        };
      } else if (prize) {
        soundJunk();
        resultName = "junk";
        const copyJunk = prizeCopy(prize.key, locale);
        nextResult = {
          kind: "junk",
          prize,
          title: copyJunk.name,
          text: copyJunk.desc,
          note: pick(t.junkQuips),
        };
      } else if (slippedRef.current) {
        soundEmpty();
        resultName = "slip";
        nextResult = {
          kind: "slip",
          prize: null,
          title: t.slipTitle,
          text: t.slipText,
          note: pick(t.slipLines),
        };
      } else {
        soundEmpty();
        resultName = "empty";
        nextResult = {
          kind: "empty",
          prize: null,
          title: t.emptyTitle,
          text: pick(t.emptyLines),
          note: t.emptyNote,
        };
      }

      emitArcadeEvent("arcade_result", {
        attempt: attemptRef.current,
        result: resultName,
        prize_name: prize?.s.name ?? null,
        reward: prize?.s.gold ? CFG.REWARD_ID : null,
      });

      setResult(nextResult);
    },
    [emitArcadeEvent, locale, soundEmpty, soundJunk, soundWin, t],
  );

  const triggerDrop = useCallback(
    (auto = false) => {
      if (stateRef.current !== "play") return;
      stateRef.current = "drop";
      pointerDownRef.current = false;
      holdLeftRef.current = false;
      holdRightRef.current = false;
      setDropDisabled(true);
      setDropLabel(auto ? t.droppingAuto : t.dropping);
      tone(300, 0.14, "triangle", 0.05);
    },
    [t, tone],
  );

  const grabCheck = useCallback(() => {
    let best: Prize | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const prize of prizesRef.current) {
      if (prize.collected || prize.falling) continue;
      const distance = Math.abs(prize.cx - clawRef.current.x);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = prize;
      }
    }

    if (best && bestDistance <= 9) {
      best.attached = true;
      attachedPrizeRef.current = best;
      const slipProbability = best.s.gold ? CFG.SLIP_GOLD : CFG.SLIP_JUNK;
      slipYRef.current =
        Math.random() < slipProbability ? 80 + Math.random() * 100 : null;
      soundClack();
      return;
    }

    attachedPrizeRef.current = null;
    slipYRef.current = null;
    soundClack();
  }, [soundClack]);

  const update = useCallback(
    (time: number, dt: number) => {
      prizesRef.current.forEach((prize) => {
        if (!prize.falling) return;

        prize.vy += 0.5;
        prize.y += prize.vy;

        if (prize.inChute) {
          if (prize.y > H + 10) {
            prize.falling = false;
            prize.collected = true;
            if (stateRef.current === "chuteWait") {
              showResult(resultPrizeRef.current);
            }
          }
          return;
        }

        if (prize.y >= FLOOR - prize.h) {
          prize.y = FLOOR - prize.h;
          prize.falling = false;
          prize.vy = 0;
        }
      });

      const claw = clawRef.current;

      switch (stateRef.current) {
        case "attract":
          claw.x = 111 + (reduceMotionRef.current ? 0 : Math.sin(time * 0.0012) * 62);
          claw.open =
            0.65 + (reduceMotionRef.current ? 0 : Math.sin(time * 0.004) * 0.3);
          break;
        case "play":
          claw.vx +=
            (holdLeftRef.current ? -0.38 : 0) +
            (holdRightRef.current ? 0.38 : 0);
          claw.vx *= 0.86;
          claw.vx = Math.max(-2.3, Math.min(2.3, claw.vx));
          claw.x += claw.vx;
          if (claw.x < PLAY_MIN) {
            claw.x = PLAY_MIN;
            claw.vx = 0;
          }
          if (claw.x > PLAY_MAX) {
            claw.x = PLAY_MAX;
            claw.vx = 0;
          }
          if (Math.abs(claw.vx) > 0.4) {
            tickThrottleRef.current += 1;
            if (tickThrottleRef.current % 7 === 0) soundTick();
          }
          dropTimeRef.current -= dt;
          setDropLabel(t.dropCountdown(Math.max(0, dropTimeRef.current).toFixed(1)));
          if (dropTimeRef.current <= 0) triggerDrop(true);
          break;
        case "drop":
          claw.y += 2.4;
          if (claw.y >= FLOOR - 14) {
            claw.y = FLOOR - 14;
            stateRef.current = "close";
          }
          break;
        case "close":
          claw.open -= 0.1;
          if (claw.open <= 0) {
            claw.open = 0;
            grabCheck();
            stateRef.current = "lift";
          }
          break;
        case "lift": {
          claw.y -= 1.7;
          const attached = attachedPrizeRef.current;
          if (attached) {
            attached.x = Math.round(claw.x - attached.w / 2);
            attached.y = Math.round(claw.y + 3);
          }
          if (attached && slipYRef.current !== null && claw.y <= slipYRef.current) {
            attached.attached = false;
            attached.falling = true;
            attached.vy = 0;
            attached.x += Math.round(Math.random() * 6 - 3);
            attachedPrizeRef.current = null;
            slippedRef.current = true;
            soundSlip();
          }
          if (claw.y <= 34) {
            claw.y = 34;
            stateRef.current = "carry";
          }
          break;
        }
        case "carry": {
          claw.x -= 1.7;
          const attached = attachedPrizeRef.current;
          if (attached) {
            attached.x = Math.round(claw.x - attached.w / 2);
            attached.y = Math.round(claw.y + 3);
          }
          if (claw.x <= CHUTE.cx) {
            claw.x = CHUTE.cx;
            stateRef.current = "openC";
          }
          break;
        }
        case "openC":
          claw.open += 0.1;
          if (claw.open >= 1) {
            claw.open = 1;
            const attached = attachedPrizeRef.current;
            if (attached) {
              attached.attached = false;
              attached.falling = true;
              attached.vy = 0;
              attached.inChute = true;
              resultPrizeRef.current = attached;
              attachedPrizeRef.current = null;
              stateRef.current = "chuteWait";
            } else {
              waitFramesRef.current = 28;
              stateRef.current = "emptyWait";
            }
          }
          break;
        case "emptyWait":
          waitFramesRef.current -= 1;
          if (waitFramesRef.current <= 0) showResult(null);
          break;
        default:
          break;
      }
    },
    [grabCheck, showResult, soundSlip, soundTick, t, triggerDrop],
  );

  const frame = useCallback(
    function animateFrame(time: number) {
      let dt = (time - lastTimeRef.current) / 1000;
      if (!lastTimeRef.current || dt > 0.05) dt = 0.016;
      lastTimeRef.current = time;
      update(time, dt);
      render(time);
      rafRef.current = requestAnimationFrame(animateFrame);
    },
    [render, update],
  );

  const startLoop = useCallback(() => {
    if (loopActiveRef.current) return;
    loopActiveRef.current = true;
    lastTimeRef.current = 0;
    rafRef.current = requestAnimationFrame(frame);
  }, [frame]);

  const stopLoop = useCallback(() => {
    if (!loopActiveRef.current) return;
    cancelAnimationFrame(rafRef.current);
    loopActiveRef.current = false;
    lastTimeRef.current = 0;
  }, []);

  const syncLoop = useCallback(() => {
    if (!document.hidden && canvasInViewRef.current) startLoop();
    else stopLoop();
  }, [startLoop, stopLoop]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    ctx.imageSmoothingEnabled = false;
    ctxRef.current = ctx;
    reduceMotionRef.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    resetRound();
    render(0);

    const observer =
      "IntersectionObserver" in window && rootRef.current
        ? new IntersectionObserver(
            (entries) => {
              canvasInViewRef.current = entries.some((entry) => entry.isIntersecting);
              syncLoop();
            },
            { threshold: 0.05 },
          )
        : null;

    if (observer && rootRef.current) observer.observe(rootRef.current);
    window.addEventListener("visibilitychange", syncLoop);
    syncLoop();

    return () => {
      observer?.disconnect();
      window.removeEventListener("visibilitychange", syncLoop);
      cancelAnimationFrame(rafRef.current);
      loopActiveRef.current = false;
      audioRef.current?.close().catch(() => undefined);
    };
  }, [render, resetRound, syncLoop]);

  useEffect(() => {
    if (!result?.prize || !previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = "#121216";
    ctx.fillRect(0, 0, 110, 110);
    const scale = Math.floor(100 / Math.max(result.prize.w, result.prize.h));
    ctx.save();
    ctx.translate(
      Math.round((110 - result.prize.w * scale) / 2),
      Math.round((110 - result.prize.h * scale) / 2),
    );
    ctx.scale(scale, scale);
    drawSprite(ctx, result.prize.s, 0, 0);
    ctx.restore();
  }, [result]);

  const startRound = useCallback(() => {
    if (creditsRef.current <= 0 || stateRef.current !== "attract") return;

    const nextCredits = creditsRef.current - 1;
    creditsRef.current = nextCredits;
    setCredits(nextCredits);
    attemptRef.current += 1;

    emitArcadeEvent("arcade_start", {
      attempt: attemptRef.current,
      credits_total: bonusClaimed
        ? CFG.CREDITS + CFG.BONUS_CREDITS
        : CFG.CREDITS,
    });

    audio();
    soundCoin();
    resetRound();
    setResult(null);
    setBonusNotice(false);
    setCoinVisible(false);
    stateRef.current = "play";
    dropTimeRef.current = CFG.DROP_SECONDS;
    setDropDisabled(false);
    setDropLabel(t.dropCountdown(CFG.DROP_SECONDS.toFixed(1)));
  }, [audio, bonusClaimed, emitArcadeEvent, resetRound, soundCoin, t]);

  useEffect(() => {
    const isTextInput = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable);

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTextInput(event.target)) return;
      if (event.key === "ArrowLeft") {
        holdLeftRef.current = true;
        event.preventDefault();
      }
      if (event.key === "ArrowRight") {
        holdRightRef.current = true;
        event.preventDefault();
      }
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        if (stateRef.current === "attract" && coinVisible && creditsRef.current > 0) {
          startRound();
        } else if (stateRef.current === "play") {
          triggerDrop(false);
        }
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (isTextInput(event.target)) return;
      if (event.key === "ArrowLeft") holdLeftRef.current = false;
      if (event.key === "ArrowRight") holdRightRef.current = false;
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [coinVisible, startRound, triggerDrop]);

  const replay = useCallback(() => {
    emitArcadeEvent("arcade_replay", {
      attempt_next: creditsRef.current > 0 ? attemptRef.current + 1 : null,
    });
    setResult(null);
    setCoinVisible(true);
    stateRef.current = "attract";
    setDropDisabled(true);
    setDropLabel(t.drop);
  }, [emitArcadeEvent, t]);

  const setSideFromPointer = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const left = event.clientX - rect.left < rect.width / 2;
    holdLeftRef.current = left;
    holdRightRef.current = !left;
  }, []);

  const stopPointer = useCallback(() => {
    pointerDownRef.current = false;
    holdLeftRef.current = false;
    holdRightRef.current = false;
  }, []);

  const onCanvasPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>) => {
      if (stateRef.current !== "play") return;
      event.preventDefault();
      pointerDownRef.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setSideFromPointer(event);
    },
    [setSideFromPointer],
  );

  const onCanvasPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!pointerDownRef.current || stateRef.current !== "play") return;
      setSideFromPointer(event);
    },
    [setSideFromPointer],
  );

  const onClaimSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const email = claimEmail.trim();
      const company = claimCompany.trim();

      if (!email) {
        setClaimState("error");
        setClaimMessage(t.missingEmail);
        return;
      }

      setClaimState("submitting");
      setClaimMessage(t.sending);

      try {
        const response = await fetch(CFG.CLAIM_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            company: company || null,
            marketingConsent: claimMarketing,
            game: "automat",
            reward: CFG.REWARD_ID,
            coupon: CFG.COUPON,
            source: "arcade_automat",
            variant,
            locale,
            form_submit_time: formatPragueTimestamp(),
            attribution_snapshot: buildAttributionSnapshot(),
          }),
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok || payload.success === false) {
          throw new Error(payload.error || "Failed to send the code.");
        }

        emitArcadeEvent("arcade_email", {
          attempt: attemptRef.current,
          reward: CFG.REWARD_ID,
          lead_source: "arcade_automat",
          marketing_consent: claimMarketing,
          email,
        });
        setClaimState("success");
        setClaimMessage(t.sentOk);
      } catch {
        setClaimState("error");
        setClaimMessage(t.sentErr);
      }
    },
    [claimCompany, claimEmail, claimMarketing, emitArcadeEvent, locale, t, variant],
  );

  const onBonusSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const email = bonusEmail.trim();

      if (!email) {
        setBonusState("error");
        setBonusMessage(t.missingEmail);
        return;
      }

      if (!bonusConsent) {
        setBonusState("error");
        setBonusMessage(t.missingConsent);
        return;
      }

      setBonusState("submitting");
      setBonusMessage(t.bonusSending);

      try {
        const response = await fetch(CFG.BONUS_CREDITS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            privacyConsent: bonusConsent,
            game: "automat",
            reward: CFG.BONUS_REWARD_ID,
            source: "arcade_automat_bonus",
            variant,
            locale,
            form_submit_time: formatPragueTimestamp(),
            attribution_snapshot: buildAttributionSnapshot(),
          }),
        });
        const payload = await response.json().catch(() => ({}));

        if (
          !response.ok ||
          payload.success === false ||
          payload.creditsGranted !== CFG.BONUS_CREDITS
        ) {
          throw new Error(payload.error || "Failed to add bonus credits.");
        }

        creditsRef.current = CFG.BONUS_CREDITS;
        setCredits(CFG.BONUS_CREDITS);
        setBonusClaimed(true);
        setBonusNotice(true);
        setBonusState("success");
        setBonusMessage(t.bonusSuccess);

        emitArcadeEvent("arcade_email", {
          attempt: attemptRef.current,
          reward: CFG.BONUS_REWARD_ID,
          lead_source: "arcade_automat_bonus",
          credits_granted: CFG.BONUS_CREDITS,
          privacy_consent: true,
        });
      } catch {
        setBonusState("error");
        setBonusMessage(t.bonusError);
      }
    },
    [bonusConsent, bonusEmail, emitArcadeEvent, locale, t, variant],
  );

  const claimStatusClass = [
    styles.claimStatus,
    claimState === "success" ? styles.claimStatusOk : "",
    claimState === "error" ? styles.claimStatusError : "",
  ]
    .filter(Boolean)
    .join(" ");

  const bonusStatusClass = [
    styles.claimStatus,
    bonusState === "success" ? styles.claimStatusOk : "",
    bonusState === "error" ? styles.claimStatusError : "",
  ]
    .filter(Boolean)
    .join(" ");

  const canStart = coinVisible && credits > 0;
  const coinNote =
    credits > 0
      ? t.coinsLeft(credits)
      : bonusClaimed
        ? t.bonusUsedText
        : t.outOfCoinsNote;
  const coinButtonLabel = credits > 0 ? t.insertCoin : t.outOfCoins;
  const privacyHref =
    locale === "en"
      ? "/en/privacy-policy"
      : "/cs/zasady-ochrany-osobnich-udaju";

  return (
    <div ref={rootRef} className={styles.root} data-arcade-machine="automat">
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.headline}>
            TMRW <span className={styles.redText}>✦ {t.machineName}</span>
          </h1>
          <div className={styles.subtitle}>{t.subtitle}</div>
        </div>
        <button
          className={[
            styles.button,
            styles.outlineButton,
            styles.muteButton,
          ].join(" ")}
          onClick={() => setMuted((value) => !value)}
          title={t.sound}
          type="button"
        >
          {muted ? "♪ OFF" : "♪ ON"}
        </button>
      </header>

      <div className={styles.cabinet}>
        <canvas
          ref={canvasRef}
          className={styles.mainCanvas}
          height={270}
          onPointerCancel={stopPointer}
          onPointerDown={onCanvasPointerDown}
          onPointerMove={onCanvasPointerMove}
          onPointerUp={stopPointer}
          width={200}
        />

        {coinVisible && (
          <div className={[styles.overlay, styles.coinOverlay].join(" ")}>
            <div className={styles.guide}>
              <div className={styles.guideHead}>
                <div className={styles.guideTitle}>{t.guideTitle}</div>
                <Image
                  className={styles.miniGif}
                  src="/images/hero-gif-1.gif"
                  alt=""
                  aria-hidden="true"
                  width={42}
                  height={42}
                  unoptimized
                />
              </div>
              {credits > 0 ? (
                <>
                  {bonusNotice ? (
                    <p className={styles.goalNote}>{t.bonusSuccess}</p>
                  ) : locale === "en" ? (
                    <>
                      <p className={styles.goalNote}>
                        Find the <b>golden coupon</b>. Win the prize.
                      </p>
                      <p>
                        <b>Golden coupon</b> = CZK 2,500 off for new clients.
                      </p>
                      <p>{t.aim}</p>
                      <p>
                        Drop the claw. You have <b>6 seconds</b>.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className={styles.goalNote}>
                        Hledej <b>zlatý kupón</b>. Najdi výhru.
                      </p>
                      <p>
                        <b>Zlatý kupón</b> = sleva 2 500 Kč pro nové klienty.
                      </p>
                      <p>{t.aim}</p>
                      <p>
                        Spusť drapák. Máš <b>6 vteřin</b>.
                      </p>
                    </>
                  )}
                  <div className={styles.credits}>{coinNote}</div>
                  <button
                    className={[
                      styles.button,
                      styles.redButton,
                      styles.guideButton,
                      canStart ? styles.blinker : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    disabled={!canStart}
                    onClick={startRound}
                    type="button"
                  >
                    {coinButtonLabel}
                  </button>
                </>
              ) : bonusClaimed ? (
                <>
                  <p className={styles.goalNote}>{t.bonusUsedTitle}</p>
                  <p>{t.bonusUsedText}</p>
                  <button
                    className={[
                      styles.button,
                      styles.redButton,
                      styles.guideButton,
                    ].join(" ")}
                    disabled
                    type="button"
                  >
                    {coinButtonLabel}
                  </button>
                </>
              ) : (
                <>
                  <p className={styles.goalNote}>{t.bonusTitle}</p>
                  <p>{t.bonusText}</p>
                  <form className={styles.claimForm} onSubmit={onBonusSubmit}>
                    <input
                      id="arcade-bonus-email"
                      name="email"
                      type="email"
                      aria-label={t.bonusEmailAriaLabel}
                      autoComplete="email"
                      inputMode="email"
                      placeholder={t.emailPlaceholder}
                      required
                      disabled={bonusState === "submitting"}
                      value={bonusEmail}
                      onChange={(event) => setBonusEmail(event.target.value)}
                    />
                    <div className={styles.bonusConsent}>
                      <input
                        id="arcade-bonus-consent"
                        name="privacyConsent"
                        type="checkbox"
                        checked={bonusConsent}
                        required
                        disabled={bonusState === "submitting"}
                        onChange={(event) =>
                          setBonusConsent(event.target.checked)
                        }
                      />
                      <label htmlFor="arcade-bonus-consent">
                        {t.bonusConsentPrefix}{" "}
                        <a
                          className={styles.privacyLink}
                          href={privacyHref}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {t.privacyPolicy}
                        </a>
                        .
                      </label>
                    </div>
                    <button
                      className={[
                        styles.button,
                        styles.redButton,
                        styles.guideButton,
                      ].join(" ")}
                      disabled={bonusState === "submitting"}
                      type="submit"
                    >
                      {t.bonusSubmit}
                    </button>
                    <p className={bonusStatusClass} aria-live="polite">
                      {bonusMessage}
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {result && (
          <div className={[styles.overlay, styles.resultOverlay].join(" ")}>
            <div
              className={[
                styles.card,
                result.kind === "gold" ? styles.goldCard : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <h2 className={result.kind === "gold" ? styles.goldTitle : ""}>
                {result.title}
              </h2>

              {result.kind === "gold" && (
                <Image
                  className={styles.rewardGif}
                  src="/images/cta-gif-1.gif"
                  alt=""
                  aria-hidden="true"
                  width={54}
                  height={54}
                  unoptimized
                />
              )}

              {result.prize && (
                <canvas
                  ref={previewCanvasRef}
                  className={[
                    styles.previewCanvas,
                    result.kind === "gold" ? styles.previewCanvasGlow : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  height={110}
                  width={110}
                />
              )}

              <p>{result.text}</p>
              {result.kind === "gold" ? (
                <>
                  <p>{t.rewardText}</p>
                  <form className={styles.claimForm} onSubmit={onClaimSubmit}>
                    <label htmlFor="arcade-claim-email">{t.emailLabel}</label>
                    <input
                      id="arcade-claim-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder={t.emailPlaceholder}
                      required
                      disabled={claimState === "success"}
                      value={claimEmail}
                      onChange={(event) => setClaimEmail(event.target.value)}
                    />
                    <label
                      className={styles.claimConsent}
                      htmlFor="arcade-claim-marketing"
                    >
                      <input
                        id="arcade-claim-marketing"
                        name="marketingConsent"
                        type="checkbox"
                        checked={claimMarketing}
                        disabled={claimState === "success"}
                        onChange={(event) =>
                          setClaimMarketing(event.target.checked)
                        }
                      />
                      <span>{t.consent}</span>
                    </label>
                    <label htmlFor="arcade-claim-company">
                      {t.companyLabel}{" "}
                      <span aria-hidden="true">{t.companyOptional}</span>
                    </label>
                    <input
                      id="arcade-claim-company"
                      name="company"
                      type="text"
                      autoComplete="organization"
                      placeholder={t.companyPlaceholder}
                      disabled={claimState === "success"}
                      value={claimCompany}
                      onChange={(event) => setClaimCompany(event.target.value)}
                    />
                    <p className={styles.claimNote}>{t.claimNote}</p>
                    <button
                      className={[styles.button, styles.redButton].join(" ")}
                      type="submit"
                      disabled={claimState === "submitting" || claimState === "success"}
                    >
                      {claimState === "success" ? t.sent : t.sendCode}
                    </button>
                    <p className={claimStatusClass} aria-live="polite">
                      {claimMessage}
                    </p>
                  </form>
                  <button
                    className={[styles.button, styles.outlineButton].join(" ")}
                    onClick={replay}
                    type="button"
                  >
                    {t.playAgain}
                  </button>
                </>
              ) : (
                <>
                  {result.note && <p className={styles.mutedText}>{result.note}</p>}
                  <button
                    className={[styles.button, styles.redButton].join(" ")}
                    onClick={replay}
                    type="button"
                  >
                    {t.tryAgain}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={styles.pad}>
        <button
          aria-label={t.left}
          className={[styles.button, styles.inkButton, styles.arrowButton].join(" ")}
          onPointerDown={() => {
            holdLeftRef.current = true;
          }}
          onPointerLeave={() => {
            holdLeftRef.current = false;
          }}
          onPointerUp={() => {
            holdLeftRef.current = false;
          }}
          type="button"
        >
          ◀
        </button>
        <button
          className={[styles.button, styles.redButton, styles.dropButton].join(" ")}
          disabled={dropDisabled}
          onClick={() => triggerDrop(false)}
          type="button"
        >
          {dropLabel}
        </button>
        <button
          aria-label={t.right}
          className={[styles.button, styles.inkButton, styles.arrowButton].join(" ")}
          onPointerDown={() => {
            holdRightRef.current = true;
          }}
          onPointerLeave={() => {
            holdRightRef.current = false;
          }}
          onPointerUp={() => {
            holdRightRef.current = false;
          }}
          type="button"
        >
          ▶
        </button>
      </div>

      <div className={styles.hint}>{t.hint}</div>
      <div className={styles.foot}>tmrw arcade</div>
    </div>
  );
}
